import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Mail, 
  Clock, 
  Eye, 
  MessageSquare, 
  Trash2, 
  ChevronRight,
  Inbox,
  CheckCircle,
  ArrowLeft
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminSubmissions = ({ user }) => {
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [adminNotes, setAdminNotes] = useState("");
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    fetchSubmissions();
  }, []);

  const fetchSubmissions = async () => {
    try {
      const response = await fetch(`${API}/admin/submissions`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setSubmissions(data || []);
      }
    } catch (error) {
      toast.error("Failed to load submissions");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (id, status) => {
    try {
      const response = await fetch(`${API}/admin/submissions/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ status, admin_notes: adminNotes })
      });

      if (response.ok) {
        toast.success("Status updated");
        fetchSubmissions();
        setShowDialog(false);
      }
    } catch (error) {
      toast.error("Failed to update");
    }
  };

  const openSubmission = (submission) => {
    setSelectedSubmission(submission);
    setAdminNotes(submission.admin_notes || "");
    setShowDialog(true);
    
    // Mark as read if new
    if (submission.status === "new") {
      handleStatusUpdate(submission.id, "read");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "new": return "bg-[#3B82F6] text-white";
      case "read": return "bg-[#F59E0B] text-[#0A0A0B]";
      case "replied": return "bg-[#22C55E] text-[#0A0A0B]";
      default: return "bg-[#71717A] text-white";
    }
  };

  const filteredSubmissions = (submissions || []).filter(s => {
    if (filter === "all") return true;
    return s.status === filter;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-submissions">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button 
            onClick={() => navigate("/dashboard")}
            className="p-2 hover:bg-[#1A1A1D] rounded-lg transition-colors"
          >
            <ArrowLeft size={20} className="text-[#A1A1AA]" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-[#FAFAFA]">Contact Submissions</h1>
            <p className="text-sm text-[#A1A1AA]">
              {submissions.filter(s => s.status === "new").length} new messages
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-2">
        {["all", "new", "read", "replied"].map((f) => (
          <Button
            key={f}
            variant={filter === f ? "default" : "outline"}
            size="sm"
            onClick={() => setFilter(f)}
            className={filter === f ? "btn-gold" : "border-[#27272A] text-[#A1A1AA]"}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
            {f !== "all" && (
              <span className="ml-1">({submissions.filter(s => s.status === f).length})</span>
            )}
          </Button>
        ))}
      </div>

      {/* Submissions List */}
      <div className="space-y-3">
        {filteredSubmissions.map((submission) => (
          <Card 
            key={submission.id}
            className="bg-[#121214] border-[#27272A] cursor-pointer hover:border-[#D4AF37]/50 transition-all"
            onClick={() => openSubmission(submission)}
            data-testid={`submission-${submission.id}`}
          >
            <CardContent className="py-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0">
                  <Mail size={24} className="text-[#D4AF37]" />
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-medium text-[#FAFAFA]">{submission.name}</h3>
                    <span className={`px-2 py-0.5 rounded-full text-xs ${getStatusColor(submission.status)}`}>
                      {submission.status}
                    </span>
                  </div>
                  <p className="text-sm text-[#A1A1AA] truncate">{submission.email}</p>
                  <p className="text-sm text-[#71717A] truncate mt-1">
                    {submission.subject || "No subject"}: {submission.message?.slice(0, 60)}...
                  </p>
                </div>

                <div className="text-right flex-shrink-0">
                  <p className="text-xs text-[#71717A]">
                    {new Date(submission.created_at).toLocaleDateString()}
                  </p>
                  <ChevronRight size={20} className="text-[#71717A] mt-2" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}

        {filteredSubmissions.length === 0 && (
          <div className="text-center py-12">
            <Inbox size={48} className="text-[#71717A] mx-auto mb-3" />
            <p className="text-[#A1A1AA]">No submissions found</p>
          </div>
        )}
      </div>

      {/* Submission Detail Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-[#121214] border-[#27272A] text-[#FAFAFA] max-w-2xl">
          <DialogHeader>
            <DialogTitle>Contact Submission</DialogTitle>
          </DialogHeader>
          
          {selectedSubmission && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-[#71717A]">Name</p>
                  <p className="text-[#FAFAFA]">{selectedSubmission.name}</p>
                </div>
                <div>
                  <p className="text-sm text-[#71717A]">Email</p>
                  <p className="text-[#FAFAFA]">{selectedSubmission.email}</p>
                </div>
                <div>
                  <p className="text-sm text-[#71717A]">Phone</p>
                  <p className="text-[#FAFAFA]">{selectedSubmission.phone || "Not provided"}</p>
                </div>
                <div>
                  <p className="text-sm text-[#71717A]">Date</p>
                  <p className="text-[#FAFAFA]">
                    {new Date(selectedSubmission.created_at).toLocaleString()}
                  </p>
                </div>
              </div>

              <div>
                <p className="text-sm text-[#71717A]">Subject</p>
                <p className="text-[#FAFAFA]">{selectedSubmission.subject || "No subject"}</p>
              </div>

              <div>
                <p className="text-sm text-[#71717A]">Message</p>
                <p className="text-[#FAFAFA] whitespace-pre-wrap bg-[#1A1A1D] p-4 rounded-lg border border-[#27272A]">
                  {selectedSubmission.message}
                </p>
              </div>

              <div>
                <p className="text-sm text-[#71717A] mb-2">Admin Notes</p>
                <Textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
                  placeholder="Add internal notes..."
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button 
                  onClick={() => handleStatusUpdate(selectedSubmission.id, "replied")}
                  className="flex-1 btn-gold"
                >
                  <CheckCircle size={16} className="mr-2" />
                  Mark as Replied
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => window.open(`mailto:${selectedSubmission.email}?subject=Re: ${selectedSubmission.subject || 'Your inquiry'}`)}
                  className="flex-1 border-[#27272A]"
                >
                  <Mail size={16} className="mr-2" />
                  Reply via Email
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminSubmissions;
